import React, { useEffect, useRef, useState } from "react";
import { Mail, MapPin, ArrowRight, CheckCircle2, Instagram, Linkedin } from "lucide-react";
import logo from "../assets/logo_h.webp";
import { CONTACT, NAV_LINKS, FOUNDER } from "../mock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export default function Contact() {
  const ref = useRef(null);
  const initial = CONTACT.form.fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {});
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in-view")),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

   const encode = (data) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");

  const onSubmit = (e) => {
    e.preventDefault();
    const missing = CONTACT.form.fields.filter((f) => f.required && !form[f.name]);
    if (missing.length) return;
    setStatus("sending");

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contacto", ...form }),
    })
      .then(() => {
        setStatus("sent");
        window.scrollTo({ top: document.getElementById("contacto").offsetTop - 60, behavior: "smooth" });
      })
      .catch(() => setStatus("error"));
  };


  const year = new Date().getFullYear();
  const inputStyle = { background: "#00101f", border: "1px solid rgba(177,205,255,0.15)" };

  const Footer = (
    <div className="mt-20 pt-10 flex flex-col md:flex-row items-center justify-between gap-6" style={{ borderTop: "1px solid rgba(177,205,255,0.12)" }}>
      <img src={logo} alt="ChiDi" className="logo-white h-7 w-auto" />
      <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
        {NAV_LINKS.map((l) => (
          <a key={l.label} href={l.href} className="text-[14px] transition-colors hover:text-white" style={{ color: "#b1cdff" }}>{l.label}</a>
        ))}
      </div>
      <p className="text-[13px]" style={{ color: "#8fb3f0" }}>{"\u00A9 "}{year}{" ChiDi \u2014 Chihuahua Digital"}</p>
    </div>
  );

  return (
    <section id="contacto" ref={ref} style={{ background: "#00101f" }}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Left: heading + subheading */}
          <div>
            <p className="reveal uppercase tracking-[0.35em] text-[12px] mb-4" style={{ color: "#e55b36" }}>{CONTACT.eyebrow}</p>
            <h2 className="reveal font-display font-semibold text-white text-[48px] md:text-[80px] leading-none">{CONTACT.heading}</h2>
            <p className="reveal mt-6 text-[17px] md:text-[19px] leading-relaxed max-w-md" style={{ color: "#b1cdff" }}>{CONTACT.subheading}</p>
            <div className="reveal mt-6 max-w-md rounded-2xl px-5 py-4" style={{ background: "rgba(229,91,54,0.08)", borderLeft: "3px solid #e55b36" }}>
              <p className="text-[15px] md:text-[16px] leading-relaxed font-medium" style={{ color: "#e55b36" }}>{CONTACT.form.intro}</p>
            </div>
          </div>

          {/* Right: qualification form */}
          <div className="reveal rounded-3xl p-7 md:p-10" style={{ background: "#001d3f", border: "1px solid rgba(177,205,255,0.12)" }}>
            {status === "sent" ? (
              <div className="py-10 text-center flex flex-col items-center">
                <CheckCircle2 size={56} style={{ color: "#e55b36" }} />
                <h3 className="font-display font-semibold text-white text-[36px] md:text-[44px] mt-5">{CONTACT.form.success.title}</h3>
                <p className="mt-4 text-[16px] leading-relaxed max-w-md" style={{ color: "#b1cdff" }}>{CONTACT.form.success.text}</p>
              </div>
            ) : (
              <>
<form onSubmit={onSubmit} className="space-y-5" name="contacto" method="POST" data-netlify="true" netlify-honeypot="bot-field" > <input type="hidden" name="form-name" value="contacto" /> <p className="hidden"> <label>No completar: <input name="bot-field" /></label> </p>
                  {CONTACT.form.fields.map((f) => (
                    <div key={f.name}>
                      <label className="block text-[14px] mb-2" style={{ color: "#b1cdff" }}>
                        {f.label}{f.required && <span style={{ color: "#e55b36" }}> *</span>}
                      </label>

                      {f.type === "text" && (
                        <input
                          name={f.name}
                          value={form[f.name]}
                          onChange={(e) => setField(f.name, e.target.value)}
                          required={f.required}
                          placeholder={f.placeholder}
                          className="w-full rounded-xl px-5 py-3.5 text-[15px] text-white outline-none transition-all focus:ring-2 focus:ring-[#e55b36]"
                          style={inputStyle}
                        />
                      )}

                      {f.type === "textarea" && (
                        <textarea
                          name={f.name}
                          value={form[f.name]}
                          onChange={(e) => setField(f.name, e.target.value)}
                          required={f.required}
                          rows={4}
                          placeholder={f.placeholder}
                          className="w-full rounded-xl px-5 py-3.5 text-[15px] text-white outline-none resize-none transition-all focus:ring-2 focus:ring-[#e55b36]"
                          style={inputStyle}
                        />
                      )}

                      {f.type === "select" && (
                        <Select value={form[f.name]} onValueChange={(v) => setField(f.name, v)}>
                          <SelectTrigger
                            className="w-full h-auto rounded-xl px-5 py-3.5 text-[15px] text-white border-0 focus:ring-2 focus:ring-[#e55b36]"
                            style={inputStyle}
                          >
                            <SelectValue placeholder={f.placeholder} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {f.options.map((o) => (
                              <SelectItem key={o} value={o} className="text-[15px]">{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {f.help && (
                        <p className="mt-2 text-[13px] leading-snug" style={{ color: "#8fb3f0" }}>{f.help}</p>
                      )}
                    </div>
                  ))}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-70"
                    style={{ background: "#e55b36" }}
                  >
                    {status === "sending" ? "Enviando..." : CONTACT.form.submitLabel}
                    {status === "idle" && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Contact info at the end of the page */}
        <div className="reveal mt-20 md:mt-28 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group rounded-2xl p-5" style={{ background: "#001d3f", border: "1px solid rgba(177,205,255,0.12)" }}>
            <span className="grid place-items-center h-12 w-12 rounded-full shrink-0" style={{ background: "#00101f", border: "1px solid rgba(177,205,255,0.15)" }}>
              <Mail size={20} style={{ color: "#e55b36" }} />
            </span>
            <span>
              <span className="block text-[13px] uppercase tracking-wider" style={{ color: "#8fb3f0" }}>Email</span>
              <span className="block text-white text-[16px] group-hover:text-[#e55b36] transition-colors break-all">{CONTACT.email}</span>
            </span>
          </a>
          <a href="https://www.instagram.com/chidi_marketing/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group rounded-2xl p-5" style={{ background: "#001d3f", border: "1px solid rgba(177,205,255,0.12)" }}>
            <span className="grid place-items-center h-12 w-12 rounded-full shrink-0" style={{ background: "#00101f", border: "1px solid rgba(177,205,255,0.15)" }}>
              <Instagram size={20} style={{ color: "#e55b36" }} />
            </span>
            <span>
              <span className="block text-[13px] uppercase tracking-wider" style={{ color: "#8fb3f0" }}>Instagram</span>
              <span className="block text-white text-[16px] group-hover:text-[#e55b36] transition-colors">@chidi_marketing</span>
            </span>
          </a>
          <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group rounded-2xl p-5" style={{ background: "#001d3f", border: "1px solid rgba(177,205,255,0.12)" }}>
            <span className="grid place-items-center h-12 w-12 rounded-full shrink-0" style={{ background: "#00101f", border: "1px solid rgba(177,205,255,0.15)" }}>
              <Linkedin size={20} style={{ color: "#e55b36" }} />
            </span>
            <span>
              <span className="block text-[13px] uppercase tracking-wider" style={{ color: "#8fb3f0" }}>LinkedIn</span>
              <span className="block text-white text-[16px] group-hover:text-[#e55b36] transition-colors">Karen Gómez</span>
            </span>
          </a>
          <div className="flex items-center gap-4 rounded-2xl p-5" style={{ background: "#001d3f", border: "1px solid rgba(177,205,255,0.12)" }}>
            <span className="grid place-items-center h-12 w-12 rounded-full shrink-0" style={{ background: "#00101f", border: "1px solid rgba(177,205,255,0.15)" }}>
              <MapPin size={20} style={{ color: "#e55b36" }} />
            </span>
            <span>
              <span className="block text-[13px] uppercase tracking-wider" style={{ color: "#8fb3f0" }}>Ubicación</span>
              <span className="block text-white text-[16px]">{CONTACT.location}</span>
            </span>
          </div>
        </div>

        {Footer}
      </div>
    </section>
  );
}
